import type { CurrentShift, Locale } from '@saniplus/contracts';
import { Button, LogIn, LogOut, Sheet, decorativeIcon } from '@saniplus/ui';
import { useTranslation } from 'react-i18next';

import { clockParts, elapsedParts, longDate } from '../lib/format';
import { FactList, type Fact } from './FactList';

/**
 * THE SECOND TAP.
 *
 * A punch is irreversible. Nothing in this product edits one — a wrong entry
 * is corrected by a supervisor with a written reason (D-11), which means a
 * pocket tap or a mis-aimed thumb costs a phone call and a paper trail. The
 * key is 88 px in the thumb zone precisely so it is easy to hit, and that is
 * exactly what makes an accidental hit likely.
 *
 * So the key no longer punches: it *asks*. This sheet is the answer, and it
 * is built for the scene rather than for the pattern:
 *
 * - **It states what will be recorded**, not merely "are you sure?". The
 *   time, the site and the shift are the three facts a worker checks before
 *   committing, and they are the three facts a supervisor would ask about
 *   afterwards.
 * - **Confirm is the punch key again** — same fill, same bevel, same 25 px
 *   label, same icon — so the gesture a worker already knows is the gesture
 *   that finishes the job. Cancel is a full-width 48 px secondary directly
 *   under it, not a text link in a corner: backing out must be as easy as
 *   going on.
 * - **It never claims the time.** The clock in the sheet is *now*; the punch
 *   is stamped by the server at the moment of the confirm and reconciled
 *   afterwards. The footnote says so, because a sheet that shows « 14 h 25 »
 *   and produces 14 h 26 has lied on a payroll screen.
 * - **No auto-dismiss, no countdown.** A worker interrupted mid-tap comes
 *   back to the same sheet, not to a punch that happened without them.
 *
 * `Esc`, the backdrop and the close control all cancel, and focus returns to
 * the punch key — `Sheet` owns all four.
 */
interface PunchConfirmSheetProps {
  /** `null` closes the sheet; a type opens it. */
  readonly action: 'in' | 'out' | null;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
  /** The shift being punched into, when the context reports one. */
  readonly shift: CurrentShift | null;
  /** The site the punch will be attributed to — the open session's on an OUT. */
  readonly site: { readonly name: string } | null;
  /** The site timezone every instant in the sheet renders in. */
  readonly timezone: string;
  readonly locale: Locale;
  /** "Now" on the server's clock, so the sheet and the rail agree. */
  readonly now: number;
  /** Elapsed since the open session, for the OUT sheet. `null` when unknown. */
  readonly elapsedMs?: number | null;
  /** The queue is holding punches — the confirm records locally, not on the server. */
  readonly offline?: boolean;
}

export function PunchConfirmSheet({
  action,
  onConfirm,
  onCancel,
  shift,
  site,
  timezone,
  locale,
  now,
  elapsedMs = null,
  offline = false,
}: PunchConfirmSheetProps) {
  const { t } = useTranslation('punch');
  const nowParts = clockParts(new Date(now).toISOString(), timezone);
  const Icon = action === 'out' ? LogOut : LogIn;

  const facts: Fact[] = [
    {
      key: 'time',
      label: t('confirm.time'),
      value: nowParts === null ? '' : t('format.clock', nowParts),
    },
  ];
  if (site !== null) {
    facts.push({ key: 'site', label: t('confirm.site'), value: site.name, prose: true });
  }
  if (shift !== null) {
    const start = clockParts(shift.startsAt, timezone);
    const end = clockParts(shift.endsAt, timezone);
    facts.push({
      key: 'shift',
      label: t('confirm.shift'),
      value:
        start === null || end === null
          ? ''
          : `${t('format.clock', start)} ${t('format.arrow')} ${t('format.clock', end)}`,
    });
    facts.push({
      key: 'date',
      label: t('confirm.date'),
      value: longDate(shift.startsAt, timezone, locale),
      prose: true,
    });
  }
  if (action === 'out' && elapsedMs !== null) {
    facts.push({
      key: 'worked',
      label: t('confirm.worked'),
      value: t('format.duration', elapsedParts(elapsedMs)),
    });
  }

  return (
    <Sheet
      open={action !== null}
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
      title={action === 'out' ? t('confirm.titleOut') : t('confirm.titleIn')}
      description={action === 'out' ? t('confirm.leadOut') : t('confirm.leadIn')}
      closeLabel={t('action.cancel')}
      footer={
        <div className="flex w-full flex-col gap-2">
          <Button
            size="xl"
            variant="primary"
            iconStart={<Icon {...decorativeIcon('key')} />}
            subLabel={
              offline
                ? t('confirm.confirmSubQueued')
                : action === 'out'
                  ? t('confirm.confirmSubOut')
                  : t('confirm.confirmSubIn')
            }
            onClick={onConfirm}
            data-testid="punch-confirm"
          >
            {action === 'out' ? t('confirm.confirmOut') : t('confirm.confirmIn')}
          </Button>
          <Button
            size="lg"
            variant="secondary"
            fullWidth
            onClick={onCancel}
            data-testid="punch-cancel"
          >
            {t('action.cancel')}
          </Button>
        </div>
      }
    >
      <FactList facts={facts} />
      <p className="mt-3 border-t border-hairline pt-3 text-body text-ink-muted">
        {offline ? t('confirm.footnoteQueued') : t('confirm.footnote')}
      </p>
    </Sheet>
  );
}
